import React from "react";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { Skeleton } from "../../atoms/Skeleton/index.js";
import { Checkbox } from "../../atoms/Checkbox/index.js";
import { EmptyState } from "../EmptyState/index.js";
import { Button } from "../../atoms/Button/Button.js";
import { CURRENT_ROW_BAR, DataTableCards } from "./DataTableCards.js";
import { SectionError } from "../AsyncSection/SectionError.js";
import {
  PIN_FIRST_COLUMN,
  PIN_FIRST_COLUMN_AFTER_SELECT,
  PIN_FIRST_COLUMN_CELL,
  PIN_FIRST_COLUMN_CONTENT,
  PIN_FIRST_COLUMN_EDGE,
  PIN_FIRST_COLUMN_ROW_STATES,
  TABLE_SCROLL_FOCUS_RING,
} from "../TableScroll/TableScroll.js";
import { useDataTableVirtual } from "./useDataTableVirtual.js";
import { DataTableCardSummary } from "./DataTableCardSummary.js";
import { treeRowProps, useDataTableTree } from "./useDataTableTree.js";
import { DataTableTreeCell } from "./DataTableTreeCell.js";
import { DataTableFolds } from "./DataTableFolds.js";
import {
  columnHeaderStyle,
  fixedTableMinWidth,
  foldedColumns,
  hideBelowClass,
  type DataTableHideBelow,
} from "./data-table-columns.js";

export type { DataTableHideBelow } from "./data-table-columns.js";

/* ---------------- DataTable (C8 flagship) ----------------
   The canonical tabular surface (strategy §15.6, §16.4).
   D1 render · D2 sorting · D3 selection · D4 keyboard grid · D5 responsive.

   D5: opt-in `responsive` renders a keyed card list below `md` (§17.5) — the
   table collapses to one card per row on phones. Pure CSS (hidden md:block /
   md:hidden), so it's SSR-safe and double-render-free for AT (the hidden view
   leaves the a11y tree). Row actions and pagination are composition, not props:
   add a trailing column whose `cell` renders a Popover/ContextMenu menu, and
   slice `data` yourself alongside our Pagination.

   Card layout is tiered, not a flat list of label/value pairs: columns declare a
   `cardRole` (see `DataTableCardRole`) and `DataTableCards` places them as
   identity → state → headline metric → labelled remainder. Declaring nothing
   still improves: the first column becomes the title and an `sr-only`-headed
   column becomes the actions slot, so no card prints a blank label. The desktop
   table is unaffected by any of it.

   `keyboardGrid` (D4) makes the table an APG grid (single tab stop, arrow nav).
   Actionable-mode (editing a cell's widget in place) is a future enhancement.

   C9 (`virtualized`): opt-in row virtualization for large datasets — only the
   visible window (+ overscan) is in the DOM. Keeps the native `<table>` (sticky
   header, auto column widths, border-collapse) by windowing the `<tbody>` with
   height-only spacer rows; the true row position is exposed to AT via
   `aria-rowcount`/`aria-rowindex` (spacer rows are `aria-hidden`). Virtualization
   is mutually exclusive with `keyboardGrid` in v1 — when both are set we warn once
   and fall back to the full non-virtualized grid so the APG refs/arrow-nav contract
   (which needs every row in the DOM) stays intact. It also takes precedence over
   `responsive`: the card list isn't windowed, so virtualization keeps the table at
   all widths rather than mounting the whole dataset as cards (warns once).

   Tree rows (`getSubRows`) make the table an APG treegrid: the keyboard grid
   is forced on, and `useDataTableTree` owns expansion, sibling sorting and the
   row attributes. Column floors (`minWidth`) and container columns
   (`hideBelow` / `foldInto`) are maths in `data-table-columns.ts`. This file
   only wires them in; without those props nothing here changes.

   Generic over the row type → documented forwardRef carve-out (§14.5); rest
   props spread to the root. */

export type SortDirection = "asc" | "desc";
export interface DataTableSort {
  columnId: string;
  direction: SortDirection;
}

/**
 * What a column *means*, so `responsive` can lay a card out with hierarchy
 * instead of N equal-weight label/value rows. Placement is ours; meaning is the
 * caller's — the table stays data-blind (§1).
 *
 * Single-slot roles (`title`, `subtitle`, `badge`, `metric`, `actions`) take the
 * first column that claims them; a second claimant degrades to `meta` rather
 * than overwriting, so a mistaken duplicate loses layout, never data.
 */
export type DataTableCardRole =
  | "title"
  | "subtitle"
  | "badge"
  | "metric"
  | "meta"
  | "actions";

export interface DataTableColumn<Row> {
  id: string;
  header: React.ReactNode;
  align?: "start" | "end" | "center";
  width?: string | number;
  cell: (row: Row) => React.ReactNode;
  sortable?: boolean;
  sortAccessor?: (row: Row) => string | number | Date | null | undefined;
  /** Card-list role under `responsive`. Defaults to `meta`; see the type. */
  cardRole?: DataTableCardRole;
  /**
   * This column's cell in the footer row, parallel to `header`. Any column
   * that sets one gives the table ONE `<tfoot>` row (a totals or summary row)
   * that follows the column's `align`; a column without one leaves its cell
   * empty. The footer sits outside the sorted, grouped or windowed body, so
   * nothing reorders it, and it shows only while data rows do (not while
   * loading, empty or failed). Under `responsive` it becomes one trailing
   * summary card after the row cards.
   */
  footer?: React.ReactNode;
  /**
   * The column's body cells name their row: they render `<th scope="row">`
   * instead of `<td>`, with the same padding, alignment and weight as any
   * other cell (a `th` is not bolded — emphasis inside `cell` stays yours).
   * When the column sets a `footer`, that footer cell is a row header too.
   */
  rowHeader?: boolean;
  /**
   * The column's floor in px. Under `layout="fixed"` the table's min-width
   * becomes the sum of the numeric widths, plus n times the largest floor
   * among the n columns without a width (they split the rest equally), plus
   * the floor of each column with a string width, so the box scrolls sideways
   * before any column shrinks below its floor. Under `layout="auto"` it is the
   * header cell's min-width.
   */
  minWidth?: number;
  /**
   * Hide this column's header and every cell while the table's OWN scroll box
   * is narrower than this container width (Tailwind v4 `@sm` 384px to `@3xl`
   * 768px) — independent of the viewport. Ignored, with a one-time dev
   * warning, under `keyboardGrid` and tree mode: a hidden cell would still be
   * a stop for the grid's arrow keys.
   */
  hideBelow?: DataTableHideBelow;
  /**
   * Another column's id. While this column is hidden by `hideBelow`, its cell
   * renders under that column's content, led by this header as sr-only text,
   * and the copy is display:none once the column shows again.
   */
  foldInto?: string;
}

export interface DataTableError {
  /** What failed, in the reader's terms — not the server's sentence. */
  readonly title: string;
  readonly description?: React.ReactNode;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}

export interface DataTableGroupBy<Row> {
  /** The group a row belongs to; consecutive rows sharing a key form a group. */
  key: (row: Row) => string;
  /** The header row's content for a group, given its key and its rows. */
  label: (key: string, rows: ReadonlyArray<Row>) => React.ReactNode;
}

export interface DataTableProps<Row> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  columns: ReadonlyArray<DataTableColumn<Row>>;
  data: ReadonlyArray<Row>;
  getRowId: (row: Row) => string;
  density?: "comfortable" | "compact";
  /**
   * Body text size: `body` (14 px, the default) or `small` (13 px — the
   * Direction B console tables, PB-5178, whose rows read as one line each).
   * The header keeps its own size either way.
   */
  textSize?: "body" | "small";
  /**
   * Paint every second body row (the 2nd, 4th, …) with a faint raised fill,
   * `bg-pb-card-elevated/20`: the zebra of the hand-built Impact/Effort table.
   * Default false. A selected or current row keeps its own fill, and hover
   * paints over the stripe.
   */
  striped?: boolean;
  /**
   * Never wrap a body cell: the box scrolls sideways instead and a column is
   * as wide as its longest value. A free-text column that must not widen the
   * table bounds itself in its own renderer (`max-w-* truncate`).
   */
  nowrap?: boolean;
  caption?: React.ReactNode;
  /**
   * Show `caption` as a visible title above the table, inside its frame.
   * Default false: the caption names the table for assistive technology only
   * (`sr-only`). Either way it is the table's `<caption>`, so it still names
   * the table and, under `pinFirstColumn`, the scroll region.
   */
  showCaption?: boolean;
  stickyHeader?: boolean;
  loading?: boolean;
  loadingRows?: number;
  emptyState?: React.ReactNode;
  /**
   * Keep the footer row while the table is empty (default false: a footer
   * shows only while data rows do). For a totals row that still reads true
   * with no rows — "Total 0" — under the empty state. Loading and failed
   * tables still have no footer.
   */
  footerWhenEmpty?: boolean;
  /**
   * The load failed. Present means failed, so half of it cannot be set.
   *
   * Rendered INSIDE the table frame, and in the card list below `md`, using
   * the same presentation `AsyncSection` uses — a table and a form must not
   * report one event two ways, and a failure that only reaches the wide layout
   * would put "No X yet" cards on a phone for a load that never happened.
   *
   * It replaces the empty state and the loading rows, but ONLY while the table
   * has no rows: a failed refetch or a failed "Load more" arrives with data
   * already on screen, and blanking the page the reader was on is a worse
   * failure than the one being reported. With rows present the table keeps
   * them and the caller reports the failure beside it.
   */
  /* `| undefined` is deliberate under the repo's `exactOptionalPropertyTypes`.
     A failure is conditional by nature, so every caller writes
     `error={query.error ? {...} : undefined}`; without the widening that is a
     type error and each of the ten table surfaces has to spread the prop in
     instead. Widening breaks no existing caller. */
  error?: DataTableError | undefined;

  sort?: DataTableSort | null;
  defaultSort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort | null) => void;

  selectable?: boolean;
  selectedIds?: ReadonlyArray<string>;
  defaultSelectedIds?: ReadonlyArray<string>;
  onSelectionChange?: (ids: string[]) => void;
  getRowLabel?: (row: Row) => string;
  renderBulkActions?: (ctx: {
    selectedIds: string[];
    clear: () => void;
  }) => React.ReactNode;

  keyboardGrid?: boolean;
  /**
   * Called with the row when it is activated: Enter on a focused row cell under
   * `keyboardGrid`, and a click anywhere on the row that did not start on an
   * interactive descendant (a button, link or input keeps its own click). Rows
   * show a pointer cursor while set. A consumer that wants keyboard reach
   * without `keyboardGrid` puts a focusable control in the row.
   */
  onRowActivate?: (row: Row) => void;
  /**
   * The current row (a wizard's active step, a tracker's current period):
   * `true` sets `aria-current="true"`, the same soft teal fill `selectable`
   * gives a selected row, and a solid teal start-edge bar in the row's first
   * cell. The bar carries the meaning for anyone who cannot tell two fills
   * apart — `aria-current` alone reaches only assistive technology. Works
   * alongside `virtualized` and `keyboardGrid`; it adds no row, so neither
   * model is affected. Under `responsive` it also marks the matching card in
   * the card list below `md` — same fill, bar and `aria-current`.
   */
  isRowCurrent?: (row: Row) => boolean;
  /**
   * A full-width row rendered directly under a row whenever this returns
   * anything but `null` — an inline detail panel, a reason, a breakdown. Its
   * cell spans every column, the `selectable` checkbox column and a
   * `pinFirstColumn` column included, and it is emitted alongside its row so
   * sorting can never separate the two. It is not a data row: select-all
   * counts data rows only. Ignored, with a one-time dev warning, under
   * `virtualized` — a windowed body has no place for a row outside its
   * measured item list — and under `keyboardGrid` (PB-5216's `groupBy`
   * precedent): a native row inside the grid is still a row to assistive
   * technology, so it would exceed the grid's `aria-rowcount` and sit
   * outside its arrow-key model.
   */
  renderRowDetail?: (row: Row) => React.ReactNode;
  /**
   * Group header rows (PB-5216): after sorting, each run of consecutive rows
   * whose `key` matches is preceded by one full-width `<th scope="rowgroup">`
   * carrying `label`. Table view only — the responsive card list stays flat.
   * Select-all still counts data rows only. Ignored, with a one-time dev
   * warning, under `virtualized` (spacer rows own the window) and under
   * `keyboardGrid` (its row indices are data rows).
   */
  groupBy?: DataTableGroupBy<Row>;

  /**
   * Turns on tree mode: a row's children, or undefined for a leaf. The table
   * becomes `role="treegrid"` with the `keyboardGrid` navigation on, rows carry
   * `aria-level`, `aria-setsize` and `aria-posinset`, and parents carry
   * `aria-expanded`. The first data column indents 16px per level and holds a
   * toggle named "Expand {getRowLabel(row)}" or "Collapse …". On a parent's
   * first cell ArrowRight expands and ArrowLeft collapses; on a child row
   * ArrowLeft moves to the parent. Sorting orders siblings within each
   * parent. Tree mode ignores `virtualized`, `groupBy`, `selectable` and
   * `responsive`, each with a one-time dev warning.
   */
  getSubRows?: (row: Row) => ReadonlyArray<Row> | undefined;
  /**
   * Uncontrolled expansion: `true` opens every parent, an id array opens
   * those rows, `false` (the default) starts collapsed.
   */
  defaultExpanded?: boolean | ReadonlyArray<string>;
  /** Controlled expansion: the ids of the open rows. Pair with `onExpandedChange`. */
  expandedIds?: ReadonlyArray<string>;
  /** Called with the ids of every open row after a row expands or collapses. */
  onExpandedChange?: (ids: string[]) => void;

  /**
   * Collapse to a keyed card list below `md` (§17.5). Default false. Ignored
   * (with a one-time dev warning) when `virtualized` is active — the card list
   * is not windowed, so virtualization shows the table at all widths instead.
   */
  responsive?: boolean;

  /**
   * Column sizing. `auto` (the default) lets the browser size every column to
   * its content, which is right for a table whose columns are all short.
   *
   * `fixed` makes the declared `width` on each column authoritative and splits
   * what is left equally between the columns that declare none. Use it whenever
   * a cell holds more than one element — a label beside a pill, a name above an
   * id — because under `auto` the widest row in the CURRENT page decides the
   * column, so the same table re-lays itself out on every page, filter and
   * refetch, and the reader's eye loses the columns it had learned.
   */
  layout?: "auto" | "fixed";

  /**
   * Window the `<tbody>` so only the visible rows render (C9). Default false.
   * Needs a bounded scroll height — see `maxHeight`. Ignored (with a one-time
   * dev warning) when `keyboardGrid` is set; takes precedence over `responsive`.
   */
  virtualized?: boolean;
  /**
   * Bounded height of the scroll container when `virtualized`. Number → px.
   * Defaults to `480` when `virtualized` and unset.
   */
  maxHeight?: number | string;
  /**
   * Row-height estimate the virtualizer starts from (auto-measurement corrects
   * it after paint). Defaults by density: comfortable `45`, compact `33`. Set
   * this when row content varies in height significantly to avoid a first-frame
   * jump.
   */
  estimateRowHeight?: number;
  /** Rows to render beyond the visible window (C9). Default `8`. */
  overscan?: number;
  /**
   * Pin the first data column while the rest scrolls horizontally inside the
   * table's own scroll box (PB-5178, d3) — the ONE sticky-first-column
   * definition `TableScroll` also uses. The box becomes a keyboard-focusable
   * region (named by a string `caption`) with a stable scrollbar gutter, and
   * the pinned cell's content is capped at half the box. With `selectable` the
   * checkbox column pins too, ahead of the data column. Ignored, with a
   * one-time dev warning, when virtualization is active: a windowed body has
   * spacer rows with no first column to pin, and no windowed table needs one.
   */
  pinFirstColumn?: boolean;
}

const ALIGN: Record<NonNullable<DataTableColumn<unknown>["align"]>, string> = {
  start: "text-start",
  center: "text-center",
  end: "text-end pb-tabular",
};

const PAGE = 10;

/* Dev-only, once per session: warn when `virtualized` is silently ignored
   because `keyboardGrid` is also set (the two are mutually exclusive in v1),
   or when `responsive` is silently dropped because `virtualized` wins (the
   card list isn't windowed, so it would re-introduce the full-DOM cost). */
let hasWarnedVirtualGrid = false;
let hasWarnedVirtualResponsive = false;
let hasWarnedVirtualPin = false;
let hasWarnedUnnamedPin = false;
let hasWarnedVirtualGroup = false;
let hasWarnedGridGroup = false;
let hasWarnedVirtualRowDetail = false;
let hasWarnedGridRowDetail = false;
let hasWarnedGridHideBelow = false;

/* A click that starts on one of these keeps its own meaning; the row does not
   also activate (the chevron a consumer adds would otherwise fire twice). */
/* What counts as "the click was for something else in this row".
 *
 * `label` is load-bearing and easy to miss: every design-system form control
 * is a `<label>` wrapping a visually hidden `<input class="sr-only">` beside
 * the box the user actually sees. A click lands on that box, whose ancestors
 * are the label — never the input, which is its SIBLING. Without `label` in
 * this list, clicking a row's own selection checkbox both selected the row and
 * activated it, and the same went for any Switch or radio in a cell. A click
 * anywhere inside a label is a click on its control by definition. */
const INTERACTIVE_DESCENDANT =
  "a, button, input, select, textarea, summary, label, [role='button'], [role='link'], [role='checkbox'], [role='switch'], [role='radio'], [role='menuitem'], [contenteditable='true']";

function clickStartedOnInteractive(
  event: React.MouseEvent<HTMLTableRowElement>,
): boolean {
  const target = event.target as Element | null;
  const hit = target?.closest(INTERACTIVE_DESCENDANT) ?? null;
  return hit !== null && event.currentTarget.contains(hit);
}

function cycleSort(
  current: DataTableSort | null,
  columnId: string,
): DataTableSort | null {
  if (!current || current.columnId !== columnId)
    return { columnId, direction: "asc" };
  if (current.direction === "asc") return { columnId, direction: "desc" };
  return null;
}

function compareValues(
  a: string | number | Date | null | undefined,
  b: string | number | Date | null | undefined,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b));
}

export function DataTable<Row>({
  columns,
  data,
  getRowId,
  density = "comfortable",
  textSize = "body",
  striped = false,
  nowrap = false,
  caption,
  showCaption = false,
  stickyHeader = true,
  loading = false,
  loadingRows = 5,
  emptyState,
  footerWhenEmpty = false,
  error,
  sort,
  defaultSort = null,
  onSortChange,
  selectable: selectableProp = false,
  selectedIds,
  defaultSelectedIds,
  onSelectionChange,
  getRowLabel,
  renderBulkActions,
  keyboardGrid: keyboardGridProp = false,
  onRowActivate,
  isRowCurrent,
  renderRowDetail,
  groupBy: groupByProp,
  getSubRows,
  defaultExpanded,
  expandedIds,
  onExpandedChange,
  responsive: responsiveProp = false,
  layout = "auto",
  virtualized: virtualizedProp = false,
  maxHeight = 480,
  estimateRowHeight,
  overscan = 8,
  pinFirstColumn = false,
  className,
  ...rest
}: DataTableProps<Row>) {
  const cellPad = density === "compact" ? "px-2.5 py-1.5" : "px-3 py-2.5";
  /* Tree mode forces the keyboard grid and wins over the props it cannot
     honour (`useDataTableTree` warns once for each one set). */
  const treeActive = getSubRows !== undefined;
  const selectable = selectableProp && !treeActive;
  const keyboardGrid = keyboardGridProp || treeActive;
  const groupBy = treeActive ? undefined : groupByProp;
  const responsive = responsiveProp && !treeActive;
  const virtualized = virtualizedProp && !treeActive;
  /* The pinned box is a focusable region named by the caption — a string on
     `aria-label`, JSX through `aria-labelledby` on the caption element. With
     no caption there is nothing to name it by, so it is not made focusable
     (a nameless tab stop is worse than none) and development says so once. */
  const captionId = React.useId();
  const captionIsText = typeof caption === "string";
  const colOffset = selectable ? 1 : 0;
  const totalCols = columns.length + colOffset;

  // ── Sorting (D2) ──
  const sortControlled = sort !== undefined;
  const [internalSort, setInternalSort] = React.useState<DataTableSort | null>(
    defaultSort,
  );
  const activeSort = sortControlled ? sort! : internalSort;
  const toggleSort = (columnId: string) => {
    const next = cycleSort(activeSort, columnId);
    if (!sortControlled) setInternalSort(next);
    onSortChange?.(next);
  };
  const sortRows = React.useCallback(
    (rows: ReadonlyArray<Row>): ReadonlyArray<Row> => {
      if (!activeSort) return rows;
      const col = columns.find((c) => c.id === activeSort.columnId);
      if (!col?.sortAccessor) return rows;
      const accessor = col.sortAccessor;
      const dir = activeSort.direction === "asc" ? 1 : -1;
      return [...rows].sort(
        (ra, rb) => compareValues(accessor(ra), accessor(rb)) * dir,
      );
    },
    [activeSort, columns],
  );
  // ── Tree rows ── the visible rows in sibling-sorted order.
  const tree = useDataTableTree({
    data,
    getRowId,
    getSubRows,
    sortRows,
    defaultExpanded,
    expandedIds,
    onExpandedChange,
    ignored: {
      virtualized: virtualizedProp,
      groupBy: groupByProp !== undefined,
      selectable: selectableProp,
      responsive: responsiveProp,
    },
  });
  const sortedData = React.useMemo(
    () => (tree.nodes ? tree.nodes.map((node) => node.row) : sortRows(data)),
    [tree.nodes, sortRows, data],
  );

  // ── Selection (D3) ──
  const selControlled = selectedIds !== undefined;
  const [internalSel, setInternalSel] = React.useState<Set<string>>(
    () => new Set(defaultSelectedIds ?? []),
  );
  const selected = selControlled ? new Set(selectedIds) : internalSel;
  const emitSel = (next: Set<string>) => {
    if (!selControlled) setInternalSel(next);
    onSelectionChange?.([...next]);
  };
  const visibleIds = sortedData.map(getRowId);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected =
    !allSelected && visibleIds.some((id) => selected.has(id));
  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emitSel(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    emitSel(next);
  };
  const clear = () => emitSel(new Set());

  // ── Footer row ──
  // Shown exactly while data rows are: loading, failed and empty tables have
  // no rows for a total to summarise — unless `footerWhenEmpty` keeps it under
  // the empty state. It is one more row for the grid and for `aria-rowcount`,
  // after the last data row.
  const footerActive =
    !loading &&
    (sortedData.length > 0 || (footerWhenEmpty && error === undefined)) &&
    columns.some((col) => col.footer !== undefined);
  const footerRow = 1 + sortedData.length;

  // ── Keyboard grid (D4) ──
  const rowCount = 1 + sortedData.length + (footerActive ? 1 : 0);
  const [activeState, setActive] = React.useState({ r: 0, c: 0 });
  /* Collapsing a branch can remove the active row; the tab stop then falls to
     the last row rather than vanishing from the grid. */
  const active = treeActive
    ? { r: Math.min(activeState.r, rowCount - 1), c: activeState.c }
    : activeState;
  const cellRefs = React.useRef(new Map<string, HTMLTableCellElement | null>());
  const setCellRef =
    (r: number, c: number) => (node: HTMLTableCellElement | null) => {
      cellRefs.current.set(`${r}:${c}`, node);
    };
  const focusCell = (r: number, c: number) =>
    cellRefs.current.get(`${r}:${c}`)?.focus();

  const activateCell = (r: number, c: number, key: string) => {
    if (r === 0) {
      if (selectable && c === 0) return toggleAll();
      const col = columns[c - colOffset];
      if (col?.sortable) toggleSort(col.id);
      return;
    }
    const row = sortedData[r - 1];
    if (!row) return;
    if (selectable && c === 0) return toggleRow(getRowId(row));
    if (key === "Enter") onRowActivate?.(row);
    else if (key === " " && selectable) toggleRow(getRowId(row));
  };

  const onGridKeyDown = (e: React.KeyboardEvent) => {
    if (!keyboardGrid) return;
    const { r, c } = active;
    if (tree.nodes && r > 0 && c === 0) {
      const to = tree.onFirstCellKey(e.key, r - 1);
      if (to !== null) {
        e.preventDefault();
        if (to >= 0) {
          setActive({ r: to + 1, c });
          focusCell(to + 1, c);
        }
        return;
      }
    }
    let nr = r;
    let nc = c;
    switch (e.key) {
      case "ArrowRight":
        nc = Math.min(c + 1, totalCols - 1);
        break;
      case "ArrowLeft":
        nc = Math.max(c - 1, 0);
        break;
      case "ArrowDown":
        nr = Math.min(r + 1, rowCount - 1);
        break;
      case "ArrowUp":
        nr = Math.max(r - 1, 0);
        break;
      case "Home":
        nc = 0;
        if (e.ctrlKey) nr = 0;
        break;
      case "End":
        nc = totalCols - 1;
        if (e.ctrlKey) nr = rowCount - 1;
        break;
      case "PageDown":
        nr = Math.min(r + PAGE, rowCount - 1);
        break;
      case "PageUp":
        nr = Math.max(r - PAGE, 0);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        activateCell(r, c, e.key);
        return;
      default:
        return;
    }
    e.preventDefault();
    if (nr !== r || nc !== c) {
      setActive({ r: nr, c: nc });
      focusCell(nr, nc);
    }
  };

  const gridCell = (
    r: number,
    c: number,
    isHeader: boolean,
    isRowHeader = false,
  ) =>
    keyboardGrid
      ? {
          role: isHeader
            ? "columnheader"
            : isRowHeader
              ? "rowheader"
              : "gridcell",
          tabIndex: active.r === r && active.c === c ? 0 : -1,
          ref: setCellRef(r, c),
          onFocus: () => setActive({ r, c }),
          "data-grid-cell": "",
        }
      : {};
  const gridCellRing = keyboardGrid
    ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pb-teal"
    : "";
  const innerTab = keyboardGrid ? -1 : undefined;

  /* The failure only takes over an EMPTY table — see the `error` prop. */
  const errorActive = error !== undefined && sortedData.length === 0;
  /* The other half of the same decision. Keeping the rows through a failed
     refetch is right — a failed *Load more* must not blank the page somebody is
     reading — but silence is not: the region then shows stale rows with nothing
     saying the last read failed and no way to try again. When there IS data the
     failure becomes a strip above the table instead of replacing it. */
  const staleNotice =
    error !== undefined && sortedData.length > 0 ? error : null;
  const isEmpty = !loading && !errorActive && sortedData.length === 0;

  // ── Virtualization (C9) ──
  const wantsVirtual = virtualized && !keyboardGrid;
  // When virtualization is active it wins over `responsive`: the card list is
  // not windowed, so rendering it would mount the whole dataset and undo the
  // DOM savings. Suppress the cards (and keep the table visible at all widths).
  const responsiveCards = responsive && !wantsVirtual;
  // The pin rides on the table's own scroll box; a windowed body has spacer
  // rows with no first column to pin, so virtualization wins (warns once).
  const pinActive = pinFirstColumn && !wantsVirtual;
  // Group header rows live between data rows; a windowed body and the grid's
  // row arithmetic both assume data rows only, so each wins over grouping.
  const groupActive = groupBy !== undefined && !wantsVirtual && !keyboardGrid;
  // A detail row is emitted alongside its own row. Like a group header it is
  // a native row the grid's count and arrow keys do not know about, and a
  // windowed body has no place for it, so each wins over detail rows too.
  const detailActive =
    renderRowDetail !== undefined && !wantsVirtual && !keyboardGrid;
  // Container columns: a display:none cell would still be an arrow-key stop
  // in the grid (and the treegrid), so the grid wins over hiding.
  const wantsHide = columns.some((col) => col.hideBelow !== undefined);
  const hideActive = wantsHide && !keyboardGrid;
  const folds = foldedColumns(columns, hideActive);
  const tableMinWidth =
    layout === "fixed"
      ? fixedTableMinWidth(columns, selectable ? 40 : 0)
      : undefined;
  React.useEffect(() => {
    if (wantsVirtual && renderRowDetail && !hasWarnedVirtualRowDetail) {
      console.warn(
        "[DataTable] `renderRowDetail` is ignored when `virtualized` is " +
          "active; a windowed body has no place for a row outside its " +
          "measured item list.",
      );
      hasWarnedVirtualRowDetail = true;
    }
    if (wantsVirtual && groupBy && !hasWarnedVirtualGroup) {
      console.warn(
        "[DataTable] `groupBy` is ignored when `virtualized` is active; " +
          "a windowed body has no place for group header rows.",
      );
      hasWarnedVirtualGroup = true;
    }
    if (keyboardGrid && renderRowDetail && !hasWarnedGridRowDetail) {
      console.warn(
        "[DataTable] `renderRowDetail` is ignored when `keyboardGrid` is " +
          "set; the grid's row indices count data rows only.",
      );
      hasWarnedGridRowDetail = true;
    }
    if (keyboardGrid && wantsHide && !hasWarnedGridHideBelow) {
      console.warn(
        "[DataTable] `hideBelow` is ignored under `keyboardGrid` and tree " +
          "mode; a hidden cell would still be a stop for the arrow keys.",
      );
      hasWarnedGridHideBelow = true;
    }
    if (keyboardGrid && groupBy && !hasWarnedGridGroup) {
      console.warn(
        "[DataTable] `groupBy` is ignored when `keyboardGrid` is set; " +
          "the grid's row indices count data rows only.",
      );
      hasWarnedGridGroup = true;
    }
    if (virtualized && keyboardGrid && !hasWarnedVirtualGrid) {
      console.warn(
        "[DataTable] `virtualized` is ignored when `keyboardGrid` is set; " +
          "rendering the full non-virtualized grid. Virtual keyboard-grid " +
          "navigation is a future enhancement.",
      );
      hasWarnedVirtualGrid = true;
    }
    if (wantsVirtual && responsive && !hasWarnedVirtualResponsive) {
      console.warn(
        "[DataTable] `responsive` is ignored when `virtualized` is set; the " +
          "card list is not windowed, so the virtualized table is shown at " +
          "all widths to keep the DOM bounded.",
      );
      hasWarnedVirtualResponsive = true;
    }
    if (wantsVirtual && pinFirstColumn && !hasWarnedVirtualPin) {
      console.warn(
        "[DataTable] `pinFirstColumn` is ignored when `virtualized` is active; " +
          "a windowed body has spacer rows with no first column to pin.",
      );
      hasWarnedVirtualPin = true;
    }
  }, [
    virtualized,
    keyboardGrid,
    responsive,
    wantsVirtual,
    pinFirstColumn,
    groupBy,
    renderRowDetail,
    wantsHide,
  ]);
  React.useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (pinFirstColumn && !caption && !hasWarnedUnnamedPin) {
      console.warn(
        "[DataTable] `pinFirstColumn` without a `caption`: the scroll box has " +
          "nothing to be named by, so it is not a keyboard-focusable region. " +
          "Pass a caption.",
      );
      hasWarnedUnnamedPin = true;
    }
  }, [pinFirstColumn, caption]);
  const { scrollRef, virtualItems, totalSize, measureElement } =
    useDataTableVirtual({
      enabled: wantsVirtual,
      count: sortedData.length,
      estimateRowHeight: estimateRowHeight ?? (density === "compact" ? 33 : 45),
      overscan,
    });
  const virtualActive = wantsVirtual && !loading && !isEmpty && !errorActive;
  // Height-only spacer rows above/below the window preserve scroll + native
  // table column sizing (translateY on a `<tr>` would require a grid/flex
  // table, abandoning the sticky-header auto-layout `<table>` this keeps).
  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];
  const padTop = firstItem ? firstItem.start : 0;
  const padBottom = lastItem ? totalSize - lastItem.end : 0;

  const defaultEmpty = (
    <EmptyState title="No data" description="There's nothing to show yet." />
  );

  // Row chrome shared by the non-virtualized and virtualized (windowed) bodies.
  const rowClassName = (isSel: boolean, isCurrent: boolean, index: number) =>
    cn(
      "border-b border-pb-border last:border-0 transition-colors",
      /* Rows arrive rather than appearing (ADR-0091) — but NEVER in the
         windowed body: the virtualizer re-mounts rows as they scroll into
         view, so every scroll would replay the entrance. */
      !virtualActive && "pb-enter-row",
      isSel || isCurrent
        ? "bg-pb-teal-soft"
        : [
            "hover:pb-overlay-paint",
            /* `striped`: counted by data row, so a detail, group or spacer
               row never shifts the stripe. */
            striped && index % 2 === 1 && "bg-pb-card-elevated/20",
          ],
      onRowActivate && "cursor-pointer",
    );
  const onRowClick = onRowActivate
    ? (row: Row) => (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (clickStartedOnInteractive(event)) return;
        onRowActivate(row);
      }
    : undefined;
  /* Runs of consecutive rows sharing a group key, in sort order; null when
     grouping is off so the plain loop stays untouched. */
  const groupedRows = React.useMemo(() => {
    if (!groupActive || !groupBy) return null;
    const runs: { key: string; rows: Row[]; start: number }[] = [];
    sortedData.forEach((row, index) => {
      const key = groupBy.key(row);
      const last = runs[runs.length - 1];
      if (last && last.key === key) last.rows.push(row);
      else runs.push({ key, rows: [row], start: index });
    });
    return runs;
  }, [groupActive, groupBy, sortedData]);
  const dataRow = (row: Row, ri: number) => {
    const r = ri + 1;
    const id = getRowId(row);
    const isSel = selected.has(id);
    const isCurrent = isRowCurrent?.(row) ?? false;
    const detail = detailActive ? (renderRowDetail!(row) ?? null) : null;
    const node = tree.nodes?.[ri];
    return (
      <React.Fragment key={id}>
        <tr
          role={keyboardGrid ? "row" : undefined}
          {...(node ? treeRowProps(node) : {})}
          aria-selected={selectable ? isSel : undefined}
          aria-current={isCurrent ? "true" : undefined}
          className={rowClassName(isSel, isCurrent, ri)}
          onClick={onRowClick?.(row)}
        >
          {rowCells(row, r, id, isSel, isCurrent)}
        </tr>
        {detail !== null && (
          <tr className="border-b border-pb-border last:border-0">
            <td colSpan={totalCols} className="p-0">
              {detail}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };
  const rowCells = (
    row: Row,
    r: number,
    id: string,
    isSel: boolean,
    isCurrent: boolean,
  ) => (
    <>
      {selectable && (
        <td
          {...gridCell(r, 0, false)}
          className={cn(cellPad, "w-10", gridCellRing, isCurrent && "relative")}
        >
          {isCurrent && <span aria-hidden className={CURRENT_ROW_BAR} />}
          <Checkbox
            aria-label={getRowLabel ? getRowLabel(row) : "Select row"}
            checked={isSel}
            onChange={() => toggleRow(id)}
            {...(innerTab !== undefined ? { tabIndex: innerTab } : {})}
          />
        </td>
      )}
      {columns.map((col, ci) => {
        const c = ci + colOffset;
        const Cell = col.rowHeader ? "th" : "td";
        const isFirstCell = !selectable && ci === 0;
        return (
          <Cell
            key={col.id}
            scope={col.rowHeader ? "row" : undefined}
            {...gridCell(r, c, false, col.rowHeader)}
            className={cn(
              col.rowHeader
                ? extendedCellClass(col, ci, "rowHeader")
                : dataCellClass(col),
              /* A pinned first cell is already sticky, which anchors the bar;
                 adding relative would make the class merge drop sticky. */
              isFirstCell && isCurrent && !pinActive && "relative",
            )}
          >
            {isFirstCell && isCurrent && (
              <span aria-hidden className={CURRENT_ROW_BAR} />
            )}
            {pinActive && ci === 0 ? (
              <div className={PIN_FIRST_COLUMN_CONTENT}>
                {cellContent(col, ci, row, r)}
              </div>
            ) : (
              cellContent(col, ci, row, r)
            )}
          </Cell>
        );
      })}
    </>
  );
  /* A body cell's content: the column's own cell, then any columns folded
     into it (`foldInto`), and in tree mode the first column's indent and
     toggle around both. Without those props it is `col.cell(row)` alone. */
  const cellContent = (
    col: DataTableColumn<Row>,
    ci: number,
    row: Row,
    r: number,
  ) => {
    const folded = folds?.get(col.id);
    const content = folded ? (
      <>
        {col.cell(row)}
        <DataTableFolds columns={folded} row={row} />
      </>
    ) : (
      col.cell(row)
    );
    const node = ci === 0 ? tree.nodes?.[r - 1] : undefined;
    if (!node) return content;
    return (
      <DataTableTreeCell
        level={node.level}
        hasChildren={node.hasChildren}
        expanded={node.expanded}
        label={getRowLabel ? getRowLabel(row) : "row"}
        onToggle={() => tree.toggle(node.id)}
        tabIndex={innerTab}
      >
        {content}
      </DataTableTreeCell>
    );
  };
  // A data column's body cell.
  const dataCellClass = (col: DataTableColumn<Row>) =>
    cn(
      cellPad,
      "align-middle",
      nowrap && "whitespace-nowrap",
      /* Under fixed sizing a cell wider than its column has nowhere to
         go, and with `nowrap` it overflowed VISIBLY across the next
         column's text. Clip it to its own box; a cell that needs to
         keep everything readable should wrap instead. */
      layout === "fixed" && "overflow-hidden",
      ALIGN[col.align ?? "start"],
      gridCellRing,
      hideBelowClass(col, hideActive),
    );
  /* A body row header or a footer cell: the body cell's classes plus a
     weight. A row header names its row, it is not a heading, so `font-normal`
     cancels the browser's bold `th` and emphasis inside `cell` stays the
     caller's; a footer is a total, emphasised by definition. The container
     pin selects `tbody td` only, so a pinned row header or footer cell wears
     the pin itself, and a pinned body row header takes its row's tints too. */
  const pinStart = selectable ? "start-10" : "start-0";
  const extendedCellClass = (
    col: DataTableColumn<Row>,
    ci: number,
    part: "rowHeader" | "footer",
  ) =>
    cn(
      dataCellClass(col),
      part === "rowHeader" ? "font-normal" : "font-medium",
      pinActive &&
        ci === 0 && [
          PIN_FIRST_COLUMN_CELL,
          pinStart,
          PIN_FIRST_COLUMN_EDGE,
          part === "rowHeader" && PIN_FIRST_COLUMN_ROW_STATES,
        ],
    );

  return (
    <div className={cn("w-full", className)} {...rest}>
      {staleNotice && (
        <div
          role="alert"
          className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-pb-red/40 bg-pb-red-soft px-3 py-2 text-body-small"
        >
          <span className="font-medium text-pb-text">{staleNotice.title}</span>
          {staleNotice.description ? (
            <span className="text-pb-text-secondary">
              {staleNotice.description}
            </span>
          ) : null}
          <span className="text-pb-text-muted">
            Showing what loaded last time.
          </span>
          {staleNotice.onRetry ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="ms-auto"
              onClick={staleNotice.onRetry}
            >
              {staleNotice.retryLabel ?? "Retry"}
            </Button>
          ) : null}
        </div>
      )}
      {selectable && renderBulkActions && selected.size > 0 && (
        <div className="mb-2 flex items-center gap-3 rounded-md border border-pb-border bg-pb-card-elevated px-3 py-2 text-sm">
          {renderBulkActions({ selectedIds: [...selected], clear })}
        </div>
      )}

      {/* ── Table (md and up) ── */}
      <div
        ref={scrollRef}
        style={virtualActive ? { maxHeight } : undefined}
        /* Pinned: the box is a size container (the content cap is in cqw), a
           keyboard tab stop (a horizontal scroller nobody can focus is
           unreachable without a mouse), and named by the caption when there is
           one. */
        tabIndex={pinActive && caption ? 0 : undefined}
        role={pinActive && caption ? "region" : undefined}
        aria-label={pinActive && captionIsText ? caption : undefined}
        aria-labelledby={
          pinActive && caption && !captionIsText ? captionId : undefined
        }
        className={cn(
          // `relative` keeps an sr-only column header (an actions column names
          // itself that way) inside this box: absolutely positioned with no
          // positioned ancestor, it would sit at the viewport's far edge and
          // widen the page behind a scrolling table.
          "relative w-full overflow-auto rounded-lg border border-pb-border bg-pb-card pb-hairline isolate",
          responsiveCards && "hidden md:block",
          pinActive &&
            `@container [scrollbar-gutter:stable] ${TABLE_SCROLL_FOCUS_RING}`,
          pinActive &&
            (selectable ? PIN_FIRST_COLUMN_AFTER_SELECT : PIN_FIRST_COLUMN),
          // `hideBelow` measures this box, never the viewport.
          hideActive && !pinActive && "@container",
        )}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions --
            in grid mode this <table> carries role="grid" and implements the APG
            grid keyboard contract; the rule can't see the conditional role so it
            false-positives. When keyboardGrid is off there is no key handler. */}
        <table
          role={treeActive ? "treegrid" : keyboardGrid ? "grid" : undefined}
          aria-rowcount={keyboardGrid || virtualActive ? rowCount : undefined}
          aria-colcount={keyboardGrid ? totalCols : undefined}
          onKeyDown={onGridKeyDown}
          style={
            tableMinWidth !== undefined
              ? { minWidth: tableMinWidth }
              : undefined
          }
          className={cn(
            "w-full border-collapse text-pb-text",
            layout === "fixed" && "table-fixed",
            textSize === "small" ? "text-body-small" : "text-sm",
          )}
        >
          {caption &&
            (showCaption ? (
              /* Visible: the table's title row, above the header, inside the
                 frame. The text pins to the start edge like a group label, so
                 a pinned table scrolling sideways keeps its title in view. */
              <caption
                id={captionId}
                className={cn(
                  cellPad,
                  "caption-top text-start font-medium text-pb-text",
                )}
              >
                <span className="sticky start-0 inline-block">{caption}</span>
              </caption>
            ) : (
              <caption id={captionId} className="sr-only">
                {caption}
              </caption>
            ))}
          <thead
            className={cn(
              "bg-pb-card-elevated text-pb-text-secondary",
              stickyHeader && "sticky top-0 z-raised",
            )}
          >
            <tr role={keyboardGrid ? "row" : undefined}>
              {selectable && (
                <th
                  scope="col"
                  {...gridCell(0, 0, true)}
                  className={cn(
                    cellPad,
                    "w-10 border-b border-pb-border",
                    gridCellRing,
                  )}
                >
                  <Checkbox
                    aria-label="Select all rows"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    {...(innerTab !== undefined ? { tabIndex: innerTab } : {})}
                  />
                </th>
              )}
              {columns.map((col, ci) => {
                const c = ci + colOffset;
                const isActive = activeSort?.columnId === col.id;
                const ariaSort = col.sortable
                  ? isActive
                    ? activeSort!.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined;
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={ariaSort}
                    style={columnHeaderStyle(col, layout)}
                    {...gridCell(0, c, true)}
                    className={cn(
                      cellPad,
                      "text-dense-2 font-semibold uppercase tracking-wider border-b border-pb-border",
                      /* A header has to obey the column too. Under fixed sizing
                         `whitespace-nowrap` let a long header run straight over
                         its neighbour at narrow widths — "TIME (GMT+5:30)" was
                         printing across ACTOR. It wraps inside its own box
                         instead; auto layout keeps the old behaviour, where the
                         column simply grows to fit. */
                      layout === "fixed"
                        ? "overflow-hidden"
                        : "whitespace-nowrap",
                      ALIGN[col.align ?? "start"],
                      gridCellRing,
                      hideBelowClass(col, hideActive),
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        tabIndex={innerTab}
                        onClick={() => toggleSort(col.id)}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-sm transition-colors hover:text-pb-text active:text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal",
                          col.align === "end" && "flex-row-reverse",
                          isActive && "text-pb-text",
                        )}
                      >
                        {col.header}
                        {isActive ? (
                          activeSort!.direction === "asc" ? (
                            <ChevronUp
                              className="w-3 h-3 shrink-0"
                              aria-hidden
                            />
                          ) : (
                            <ChevronDown
                              className="w-3 h-3 shrink-0"
                              aria-hidden
                            />
                          )
                        ) : (
                          <ChevronsUpDown
                            className="w-3 h-3 shrink-0 text-pb-text-muted"
                            aria-hidden
                          />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading &&
              !errorActive &&
              Array.from({ length: loadingRows }).map((_, r) => (
                <tr
                  key={`sk-${r}`}
                  className="border-b border-pb-border last:border-0"
                >
                  {selectable && (
                    <td className={cellPad}>
                      <Skeleton className="h-4 w-4" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cn(cellPad, hideBelowClass(col, hideActive))}
                    >
                      <Skeleton className="h-4 w-full max-w-[12rem]" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading &&
              !virtualActive &&
              (groupedRows && groupBy
                ? groupedRows.map((group, gi) => (
                    <React.Fragment key={`group-${gi}-${group.key}`}>
                      <tr className="bg-pb-card-elevated/60">
                        <th
                          scope="rowgroup"
                          colSpan={totalCols}
                          className="border-b border-pb-border px-3 py-1.5 text-start text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted"
                        >
                          {/* The label stays in view while the region scrolls
                              sideways: the cell spans every column, so it is
                              as wide as the table, and the span pins to the
                              start edge inside it. */}
                          <span className="sticky start-0 inline-block">
                            {groupBy.label(group.key, group.rows)}
                          </span>
                        </th>
                      </tr>
                      {group.rows.map((row, ri) =>
                        dataRow(row, group.start + ri),
                      )}
                    </React.Fragment>
                  ))
                : sortedData.map((row, ri) => dataRow(row, ri)))}

            {/* Windowed body (C9): spacer rows reserve the off-screen height so
                native column sizing + the sticky header are untouched; the true
                position is announced via aria-rowindex (spacers are hidden). */}
            {!loading && virtualActive && (
              <>
                {padTop > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={totalCols}
                      className="border-0 p-0"
                      style={{ height: padTop }}
                    />
                  </tr>
                )}
                {virtualItems.map((vi) => {
                  const row = sortedData[vi.index];
                  if (!row) return null;
                  const id = getRowId(row);
                  const isSel = selected.has(id);
                  const isCurrent = isRowCurrent?.(row) ?? false;
                  return (
                    <tr
                      key={id}
                      ref={measureElement}
                      data-index={vi.index}
                      aria-rowindex={vi.index + 2}
                      aria-selected={selectable ? isSel : undefined}
                      aria-current={isCurrent ? "true" : undefined}
                      className={rowClassName(isSel, isCurrent, vi.index)}
                      onClick={onRowClick?.(row)}
                    >
                      {rowCells(row, vi.index + 1, id, isSel, isCurrent)}
                    </tr>
                  );
                })}
                {padBottom > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={totalCols}
                      className="border-0 p-0"
                      style={{ height: padBottom }}
                    />
                  </tr>
                )}
              </>
            )}

            {errorActive && error && (
              <tr>
                <td colSpan={totalCols} className="p-0">
                  <SectionError
                    title={error.title}
                    description={error.description}
                    {...(error.onRetry ? { onRetry: error.onRetry } : {})}
                    {...(error.retryLabel
                      ? { retryLabel: error.retryLabel }
                      : {})}
                  />
                </td>
              </tr>
            )}

            {isEmpty && (
              <tr>
                <td colSpan={totalCols} className="p-0">
                  {emptyState ?? defaultEmpty}
                </td>
              </tr>
            )}
          </tbody>
          {/* Outside the body, so sorting, grouping and the virtual window
              never move it; it is the grid's last row and, when windowed,
              carries its true position like the data rows do. */}
          {footerActive && (
            <tfoot>
              <tr
                role={keyboardGrid ? "row" : undefined}
                aria-rowindex={virtualActive ? rowCount : undefined}
                className="border-t border-pb-border"
              >
                {selectable && (
                  <td
                    {...gridCell(footerRow, 0, false)}
                    className={cn(
                      cellPad,
                      "w-10",
                      gridCellRing,
                      pinActive && [PIN_FIRST_COLUMN_CELL, "start-0"],
                    )}
                  />
                )}
                {columns.map((col, ci) => {
                  // An empty header names nothing, so only a row-header
                  // column that sets a footer gets a footer `th`.
                  const isRowHeader =
                    col.rowHeader === true && col.footer !== undefined;
                  const Cell = isRowHeader ? "th" : "td";
                  return (
                    <Cell
                      key={col.id}
                      scope={isRowHeader ? "row" : undefined}
                      {...gridCell(
                        footerRow,
                        ci + colOffset,
                        false,
                        isRowHeader,
                      )}
                      className={extendedCellClass(col, ci, "footer")}
                    >
                      {pinActive && ci === 0 ? (
                        <div className={PIN_FIRST_COLUMN_CONTENT}>
                          {col.footer}
                        </div>
                      ) : (
                        col.footer
                      )}
                    </Cell>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── Card list (below md) — §17.5 ── */}
      {responsiveCards && (
        <DataTableCards
          columns={columns}
          data={sortedData}
          getRowId={getRowId}
          loading={loading}
          loadingRows={loadingRows}
          isEmpty={isEmpty}
          emptyState={emptyState ?? defaultEmpty}
          {...(errorActive && error ? { error } : {})}
          selectable={selectable}
          selected={selected}
          onToggleRow={toggleRow}
          getRowLabel={getRowLabel}
          isRowCurrent={isRowCurrent}
        />
      )}
      {responsiveCards && footerActive && (
        <DataTableCardSummary columns={columns} />
      )}
    </div>
  );
}
